import React from 'react'

import { useAioFeedback } from '../../../../shared/hooks/useAioFeedback'
import type { ThemeMode } from '@/hooks/useLightMode'
import { useTranslation } from 'react-i18next'

import { Feedback } from '@hivewatch/dark'

import type { IncidentActionType } from '@/graphql/generated/schemas'

interface AioFeedbackIProps {
  incidentId: string
  /** Thumb state hydrated from `incident.aiOperatorLatestFeedback`. */
  initialIsPositive?: boolean | null
  colorScheme: ThemeMode
  /** Refetch the trail query so the widget re-hydrates after a write. */
  onSubmitted?: () => void | Promise<unknown>
  /** Trail outcome (RESOLVED/FLAG), attached to feedback analytics events. */
  outcomeKind?: IncidentActionType
}

/**
 * Incident-drawer container for the AI Operator feedback widget. Owns the
 * write/refetch flow via `useAioFeedback` and renders the presentational
 * `@hivewatch/dark` Feedback. Mounted inside the ReasoningTrail card footer,
 * which already provides the top divider, so it is gated on the
 * same condition as the card.
 */
export const AioFeedback = ({
  incidentId,
  initialIsPositive,
  colorScheme,
  onSubmitted,
  outcomeKind,
}: AioFeedbackIProps) => {
  const { t } = useTranslation()
  const {
    selected: isPositiveSelection,
    comment,
    onCommentChange,
    onThumbClick,
    onSubmitComment,
    isSubmitting,
    hasRecordedFeedback,
  } = useAioFeedback({
    incidentId,
    initialIsPositive,
    onSubmitted,
    outcomeKind,
    errorMessage: t('ai_operator:reasoning_trail_feedback_error'),
  })

  return (
    <Feedback
      colorScheme={colorScheme}
      commentPlaceholder={t(
        'ai_operator:reasoning_trail_feedback_comment_placeholder'
      )}
      commentValue={comment}
      isSubmitting={isSubmitting}
      negativeAriaLabel={t('ai_operator:reasoning_trail_feedback_thumb_down')}
      onCommentChange={onCommentChange}
      onSubmitComment={onSubmitComment}
      onThumbClick={onThumbClick}
      positiveAriaLabel={t('ai_operator:reasoning_trail_feedback_thumb_up')}
      question={t('ai_operator:reasoning_trail_feedback_question')}
      recordedLabel={t('ai_operator:reasoning_trail_feedback_recorded')}
      selected={isPositiveSelection}
      showRecordedConfirmation={hasRecordedFeedback}
      submitLabel={t('ai_operator:reasoning_trail_feedback_submit')}
    />
  )
}
